import { useEffect, useRef, useState } from 'react'
import { fetchWeatherHistoryMonths, fetchWeatherHistory, fetchWeatherForecast } from '../utils/weather'
import '../styles/WeatherTimeline.css'

const MONTHS_FR = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.']

// Monthly weather timeline (S9): replay the archived per-region weather stored by
// the backend. Shown while the weather layer is active. Selecting a month feeds
// that month's snapshot to the map (via onWeatherData); "Direct" returns to live.
export default function WeatherTimeline({ active, onWeatherData }) {
  const [months, setMonths] = useState([])   // [{ year, month }]
  const [index, setIndex] = useState(null)    // null = live
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(false)
  const playRef = useRef(null)

  useEffect(() => {
    if (!active) return
    let cancelled = false
    Promise.all([fetchWeatherHistoryMonths(), fetchWeatherForecast()]).then(([hist, fc]) => {
      if (cancelled) return
      const list = [...hist]
      // Append the ghost forecast as a pseudo-month after the last archived one.
      if (fc) {
        const last = hist[hist.length - 1]
        if (!last || fc.year > last.year || (fc.year === last.year && fc.month > last.month)) {
          list.push({ year: fc.year, month: fc.month, ghost: true })
        }
      }
      setMonths(list)
    })
    return () => { cancelled = true }
  }, [active])

  // Fetch + push the selected month's snapshot (history, or the ghost forecast,
  // or live when index is null).
  useEffect(() => {
    if (!active) return
    if (index == null) { onWeatherData(null); return }
    const m = months[index]
    if (!m) return
    let cancelled = false
    setLoading(true)
    const p = m.ghost ? fetchWeatherForecast() : fetchWeatherHistory(m.year, m.month)
    p.then((data) => {
      if (cancelled) return
      setLoading(false)
      if (data) onWeatherData(data)
    })
    return () => { cancelled = true }
  }, [active, index, months, onWeatherData])

  // Auto-advance while playing.
  useEffect(() => {
    if (!playing || !months.length) return
    playRef.current = setInterval(() => {
      setIndex((i) => {
        const next = (i == null ? 0 : i + 1)
        if (next >= months.length) return months.length - 1
        return next
      })
    }, 1400)
    return () => clearInterval(playRef.current)
  }, [playing, months.length])

  // Stop playing at the end, and reset override when the layer turns off.
  useEffect(() => {
    if (index != null && index >= months.length - 1) setPlaying(false)
  }, [index, months.length])
  useEffect(() => {
    if (!active) { setPlaying(false); setIndex(null); onWeatherData(null) }
  }, [active, onWeatherData])

  if (!active || !months.length) return null

  const current = index != null ? months[index] : null
  const isGhost = !!current?.ghost
  const label = current
    ? `${MONTHS_FR[current.month - 1]} ${current.year}${isGhost ? ' · prévision' : ''}`
    : 'Direct (temps réel)'

  return (
    <div className={`weather-timeline pr-widget${isGhost ? " wt-ghost" : ""}`}>
      <button
        type="button"
        className={`wt-btn wt-live ${index == null ? 'active' : ''}`}
        onClick={() => { setPlaying(false); setIndex(null) }}
        title="Météo en direct"
      >⏺ Direct</button>

      <button
        type="button"
        className="wt-btn"
        onClick={() => setPlaying((p) => !p)}
        disabled={index != null && index >= months.length - 1 && !playing}
        title={playing ? 'Pause' : 'Rejouer l\'historique'}
      >{playing ? '⏸' : '▶'}</button>

      <input
        type="range"
        className="wt-slider"
        min="0"
        max={months.length - 1}
        step="1"
        value={index == null ? 0 : index}
        onChange={(e) => { setPlaying(false); setIndex(parseInt(e.target.value, 10)) }}
      />

      <span className="wt-label">
        {label}{loading && <span className="wt-loading"> …</span>}
      </span>
    </div>
  )
}
